import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  ICallFlow,
  IClinicE911Address,
  IClinicInternalExtension,
  IClinicNumber,
  IClinicPhoneDevice,
  ITwilioPortRequest,
} from '@dataTypes/clinic.type'
import {
  cancelPortRequestAction,
  createCallFlowAction,
  createE911AddressAction,
  createDeviceE911AddressAction,
  createInternalExtensionAction,
  createPhoneDeviceAction,
  deleteCallFlowAction,
  deleteClinicNumberAction,
  deleteE911AddressAction,
  deleteInternalExtensionAction,
  deletePhoneDeviceAction,
  updatePhoneDeviceAction,
  fetchCallFlowsAction,
  fetchPhoneManagementData,
  purchaseTwilioNumberAction,
  refreshPortRequestStatusAction,
  releaseClinicNumberAction,
  resubmitPortRequestAction,
  resubmitPortRequestDbAction,
  resubmitFaxPortRequestDbAction,
  submitFaxPortRequestDbAction,
  submitPortRequestDbAction,
  updateCallFlowAction,
  updateClinicNumberAction,
  bulkUpdateClinicNumbersWorkingHoursAction,
  updateE911AddressAction,
  updateInternalExtensionAction,
} from '@store/actions/settings-communication.action'

type SettingsCommunicationState = {
  clinicNumbers: IClinicNumber[]
  internalExtensions: IClinicInternalExtension[]
  e911Addresses: IClinicE911Address[]
  phoneDevices: IClinicPhoneDevice[]
  portRequests: ITwilioPortRequest[]
  callFlows: ICallFlow[]
  loading: boolean
  error: string | null
}

const initialState: SettingsCommunicationState = {
  clinicNumbers: [],
  internalExtensions: [],
  e911Addresses: [],
  phoneDevices: [],
  portRequests: [],
  callFlows: [],
  loading: false,
  error: null,
}

const settingsCommunicationSlice = createSlice({
  name: 'settingsCommunication',
  initialState,
  reducers: {
    clearSettingsCommunicationError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPhoneManagementData.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPhoneManagementData.fulfilled, (state, action) => {
        state.loading = false
        state.clinicNumbers = action.payload.clinicNumbers
        state.internalExtensions = action.payload.internalExtensions
        state.e911Addresses = action.payload.e911Addresses
        state.phoneDevices = action.payload.phoneDevices
        state.portRequests = action.payload.portRequests
      })
      .addCase(fetchPhoneManagementData.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload || 'Failed to fetch phone data') as string
      })
      .addCase(purchaseTwilioNumberAction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(purchaseTwilioNumberAction.fulfilled, (state) => {
        state.loading = false
        state.error = null
      })
      .addCase(purchaseTwilioNumberAction.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload || 'Failed to purchase number') as string
      })
      .addCase(
        deleteClinicNumberAction.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.clinicNumbers = state.clinicNumbers.filter(
            (n) => n.clinic_number_id !== action.payload,
          )
        },
      )
      .addCase(updateClinicNumberAction.fulfilled, (state, action) => {
        const idx = state.clinicNumbers.findIndex(
          (n) => n.clinic_number_id === action.payload.clinic_number_id,
        )
        if (idx !== -1) {
          state.clinicNumbers[idx] = {
            ...state.clinicNumbers[idx],
            ...action.payload,
          }
        }
      })
      .addCase(
        bulkUpdateClinicNumbersWorkingHoursAction.fulfilled,
        (state, action) => {
          for (const updated of action.payload) {
            const idx = state.clinicNumbers.findIndex(
              (n) => n.clinic_number_id === updated.clinic_number_id,
            )
            if (idx !== -1) {
              state.clinicNumbers[idx] = {
                ...state.clinicNumbers[idx],
                ...updated,
              }
            }
          }
        },
      )
      .addCase(
        deleteInternalExtensionAction.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.internalExtensions = state.internalExtensions.filter(
            (e) => e.clinic_internal_extension_id !== action.payload,
          )
        },
      )
      .addCase(createInternalExtensionAction.fulfilled, (state, action) => {
        state.internalExtensions.unshift(action.payload)
      })
      .addCase(createPhoneDeviceAction.fulfilled, (state, action) => {
        state.phoneDevices.unshift(action.payload.phone_device)
      })
      .addCase(updatePhoneDeviceAction.fulfilled, (state, action) => {
        const idx = state.phoneDevices.findIndex(
          (d) =>
            d.clinic_phone_device_id === action.payload.clinic_phone_device_id,
        )
        if (idx >= 0) state.phoneDevices[idx] = action.payload
      })
      .addCase(
        deletePhoneDeviceAction.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.phoneDevices = state.phoneDevices.filter(
            (d) => d.clinic_phone_device_id !== action.payload,
          )
        },
      )
      .addCase(createDeviceE911AddressAction.fulfilled, (state, action) => {
        const e911 = action.payload.e911_address
        const idx = state.e911Addresses.findIndex(
          (a) => a.clinic_e911_address_id === e911.clinic_e911_address_id,
        )
        if (idx >= 0) state.e911Addresses[idx] = e911
        else state.e911Addresses.unshift(e911)

        const device = state.phoneDevices.find((d) =>
          d.clinic_phone_device_id && e911.clinic_e911_address_id
            ? d.clinic_e911_address_id === e911.clinic_e911_address_id || false
            : false,
        )
        if (device && e911.clinic_e911_address_id) {
          device.clinic_e911_address_id = e911.clinic_e911_address_id
        }
      })
      .addCase(updateInternalExtensionAction.fulfilled, (state, action) => {
        const idx = state.internalExtensions.findIndex(
          (e) =>
            e.clinic_internal_extension_id ===
            action.payload.clinic_internal_extension_id,
        )
        if (idx !== -1) {
          state.internalExtensions[idx] = {
            ...state.internalExtensions[idx],
            ...action.payload,
          }
        }
      })
      .addCase(
        deleteE911AddressAction.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.e911Addresses = state.e911Addresses.filter(
            (e) => e.clinic_e911_address_id !== action.payload,
          )
        },
      )
      .addCase(createE911AddressAction.fulfilled, (state, action) => {
        state.e911Addresses.unshift(action.payload)
      })
      .addCase(updateE911AddressAction.fulfilled, (state, action) => {
        const idx = state.e911Addresses.findIndex(
          (e) =>
            e.clinic_e911_address_id === action.payload.clinic_e911_address_id,
        )
        if (idx !== -1) {
          state.e911Addresses[idx] = {
            ...state.e911Addresses[idx],
            ...action.payload,
          }
        }
      })
      .addCase(submitPortRequestDbAction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(submitPortRequestDbAction.fulfilled, (state, action) => {
        state.loading = false
        state.error = null
        state.portRequests = [action.payload, ...state.portRequests]
      })
      .addCase(submitPortRequestDbAction.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload ||
          'Failed to save port request') as string
      })
      .addCase(submitFaxPortRequestDbAction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(submitFaxPortRequestDbAction.fulfilled, (state, action) => {
        state.loading = false
        state.error = null
        state.portRequests = [action.payload, ...state.portRequests]
      })
      .addCase(submitFaxPortRequestDbAction.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload ||
          'Failed to save fax port request') as string
      })
      .addCase(resubmitFaxPortRequestDbAction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(resubmitFaxPortRequestDbAction.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload ||
          'Failed to resubmit fax port request') as string
      })
      .addCase(resubmitFaxPortRequestDbAction.fulfilled, (state) => {
        state.loading = false
        state.error = null
      })
      .addCase(resubmitPortRequestDbAction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(resubmitPortRequestDbAction.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload ||
          'Failed to resubmit port request') as string
      })
      .addCase(resubmitPortRequestDbAction.fulfilled, (state) => {
        state.loading = false
        state.error = null
      })
      .addCase(resubmitPortRequestAction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(resubmitPortRequestAction.fulfilled, (state, action) => {
        state.loading = false
        state.error = null
        state.portRequests = [
          action.payload,
          ...state.portRequests.filter(
            (p) => p.request_group_id !== action.payload.request_group_id,
          ),
        ]
      })
      .addCase(resubmitPortRequestAction.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload ||
          'Failed to resubmit port request') as string
      })
      .addCase(refreshPortRequestStatusAction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(refreshPortRequestStatusAction.fulfilled, (state) => {
        state.loading = false
        state.error = null
      })
      .addCase(refreshPortRequestStatusAction.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload ||
          'Failed to refresh port request') as string
      })
      .addCase(cancelPortRequestAction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(cancelPortRequestAction.fulfilled, (state) => {
        state.loading = false
        state.error = null
      })
      .addCase(cancelPortRequestAction.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload ||
          'Failed to cancel port request') as string
      })
      .addCase(releaseClinicNumberAction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(releaseClinicNumberAction.fulfilled, (state) => {
        state.loading = false
        state.error = null
      })
      .addCase(releaseClinicNumberAction.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload || 'Failed to release number') as string
      })
      .addCase(fetchCallFlowsAction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCallFlowsAction.fulfilled, (state, action) => {
        state.loading = false
        state.callFlows = action.payload
      })
      .addCase(fetchCallFlowsAction.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload || 'Failed to load call flows') as string
      })
      .addCase(createCallFlowAction.fulfilled, (state, action) => {
        state.callFlows = [action.payload, ...state.callFlows]
      })
      .addCase(updateCallFlowAction.fulfilled, (state, action) => {
        const idx = state.callFlows.findIndex(
          (f) => f.call_flow_id === action.payload.call_flow_id,
        )
        if (idx !== -1) {
          state.callFlows[idx] = action.payload
        }
      })
      .addCase(deleteCallFlowAction.fulfilled, (state, action) => {
        state.callFlows = state.callFlows.filter(
          (f) => f.call_flow_id !== action.payload,
        )
      })
  },
})

export const { clearSettingsCommunicationError } =
  settingsCommunicationSlice.actions
export default settingsCommunicationSlice.reducer
